import { ChangeEvent, FC, Fragment } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Components
import MultiLanguageTextField from 'components/MultiLanguageTextField';
import { Checkbox, Select } from 'components/Controls';
import SectionConsents from './SectionConsents';
import CustomFields from './CustomFields';
import TeamSelectorField from './TeamSelectorField';
// Utilities
import { isMaxValue } from 'utilities/validation';

// Mounted only while registrationPage exists: useFieldArray crashes on reset
// when the parent object is null ('none').
const RegistrationFields:FC = () => {
  const { control } = useFormContext();

  // Shared across TeamSelectorField (binding: 'team' entry) and CustomFields
  // (everything else) so both operate on the same registrationPage.fields array
  // through one useFieldArray instance.
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'registrationPage.fields',
  });

  return (
    <Fragment>
      <TeamSelectorField fields={fields} append={append} remove={remove} />
      <CustomFields fields={fields} append={append} remove={remove} />
    </Fragment>
  )
}

const RegistrationForm:FC = () => {
  const { t } = useTranslation();

  const { control, watch, setValue, formState: { errors } } = useFormContext();

  const watchedRegistrationPage = watch('registrationPage');

  const registrationLabels:Record<string, string> = {
    'anonymous': t('labels.anonymous'),
    'required': t('labels.required'),
    'none': t('labels.none'),
  }

  const handleChangeRegistration = (event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (value === 'none') {
      setValue('registrationPage', null);
      return;
    }

    const nextData = watchedRegistrationPage ? { ...watchedRegistrationPage } : {
      anonymous: false,
      caption: '',
      showPriorQuiz: false,
    }

    if (value === 'anonymous') {
      setValue('registrationPage', { ...nextData, anonymous: true });
    } else {
      setValue('registrationPage', { ...nextData, anonymous: false });
    }
  }

  return (
    <div className="w-full">
      <Select
        id="registration" name="registration"
        label={t('pages.quizForm.registrationLabel')}
        options={['anonymous', 'required', 'none'].map(field => (
          { value: field, label: registrationLabels[field] }
        ))}
        value={!watchedRegistrationPage ? 'none' : watchedRegistrationPage.anonymous ? 'anonymous' : 'required'}
        onChange={handleChangeRegistration}
      />
      {!!watchedRegistrationPage ? (
        <Fragment>
          <Controller
            control={control} name="registrationPage.showPriorQuiz"
            render={({ field: { onChange, value, ...field } }) => (
              <Select
                {...field}
                label={t('pages.quizForm.showRegisterScreenLabel')} id="registrationPriorQuiz" name="registrationPriorQuiz"
                options={[
                  { value: 'true', label: t('pages.quizForm.priorQuizLabel') },
                  { value: 'false', label: t('pages.quizForm.afterQuizLabel') },
                ]}
                // the stored value stays a real boolean - only the native
                // <select> needs a string, converted right here so 'true'/
                // 'false' strings never leak into shared form state (where
                // "false" would be truthy and break other checks).
                value={value ? 'true' : 'false'}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.value === 'true')}
              />
            )}
          />
        </Fragment>
      ) : null}
      {!!watchedRegistrationPage && !watchedRegistrationPage.anonymous ? (
        <Fragment>
          <MultiLanguageTextField
            rules={{ validate: { maxLength: (value: string) => isMaxValue(value, 120) } }}
            name="registrationPage.caption"
            label={t('pages.quizForm.registrationCaptionLabel')}
            error={Boolean(errors.registrationPage?.caption)}
            errorText={errors.registrationPage?.caption ? errors.registrationPage.caption.message : ''}
            helperText={t('pages.quizForm.registrationCaptionHint')}
            className="mb-4"
          />
          <Controller
            control={control} name="registrationPage.showDateOfBirth"
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="showDateOfBirth" name="showDateOfBirth"
                label={t('pages.quizForm.showDateOfBirthLabel')}
                checked={value}
                onChange={onChange}
                helperText={t('pages.quizForm.showDateOfBirthHint')}
              />
            )}
          />
          <Controller
            control={control} name="registrationPage.requirePhoneNumber"
            render={({ field: { onChange, value } }) => (
              <Checkbox
                id="requirePhoneNumber" name="requirePhoneNumber"
                label={t('pages.quizForm.requirePhoneNumberLabel')}
                checked={value}
                onChange={onChange}
                helperText={t('pages.quizForm.requirePhoneNumberHint')}
              />
            )}
          />
        </Fragment>
      ) : null}
      {!!watchedRegistrationPage && watchedRegistrationPage.anonymous ? (
        <Controller
          control={control} name="registrationPage.useExistingPlayer"
          render={({ field: { onChange, value } }) => (
            <Checkbox
              id="useExistingPlayer" name="useExistingPlayer"
              label={t('pages.quizForm.useExistingPlayerLabel')}
              checked={value}
              onChange={onChange}
              helperText={t('pages.quizForm.useExistingPlayerHint')}
            />
          )}
        />
      ) : null}
      {!!watchedRegistrationPage ? <RegistrationFields /> : null}
      <SectionConsents />
    </div>
  )
}

export default RegistrationForm;
